import React, { useState, useRef } from 'react'
import { Chart } from 'primereact/chart'
import { Checkbox } from 'primereact/checkbox'
import { OverlayPanel } from 'primereact/overlaypanel'
import { useTranslation } from 'react-i18next'

import './StackedChart.scss'
import SwitchButtonComponent from '../../chart-components/single-selection-button/SwitchButtonComponent'
import { ChartItems } from 'types/DashboardType'

const StackedChart = (props: {
  colors: any
  labels: string[]
  chartId: string
  tooltipLabel: string
  userInfo: any
  tabIndex: number
  response: ChartItems | undefined
  checkedValue: any
  setCheckedValue: any
}) => {
  const { t } = useTranslation()

  const [selected, setSelected] = useState<any>(t('dashboard.count'))
  const opProperties = useRef<OverlayPanel>(null)
  const showProperties = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!opProperties?.current?.state?.visible) {
      opProperties.current?.toggle(e, e.target)
    }
  }
  const hideProperties = (e: React.MouseEvent<HTMLDivElement>) => {
    opProperties?.current?.hide()
  }
  const onCheckedValueChange = (e: { value: any; checked: boolean }) => {
    let selectedValue = [...props.checkedValue]
    if (e.checked) {
      selectedValue.splice(0, 1, e.value)
    }
    props.setCheckedValue(selectedValue)
  }

  const addEmptyLabels = (array: any) => {
    const maxCount = props.userInfo === 'LocationAdmin' ? 10 : 6
    if (array && array.length > 0 && array.length < 5) {
      for (let i = 0; i < maxCount - array.length; i++) {
        array.push('')
      }
    }

    return array
  }

  const stackedData = {
    labels: addEmptyLabels(props?.response?.stackedItems.labels),
    datasets: [
      {
        label: props.labels[0],
        backgroundColor: props.colors[0],
        data:
          selected === t('dashboard.percentage')
            ? props.response?.stackedItems.percentage[0]
            : props.response?.stackedItems.count[0],
      },
      {
        label: props.labels[1],
        backgroundColor: props.colors[1],
        data:
          selected === t('dashboard.percentage')
            ? props.response?.stackedItems.percentage[1]
            : props.response?.stackedItems.count[1],
      },
      {
        label: props.labels[2],
        backgroundColor: props.colors[2],
        data:
          selected === t('dashboard.percentage')
            ? props.response?.stackedItems.percentage[2]
            : props.response?.stackedItems.count[2],
      },
      {
        label: props.labels[3],
        backgroundColor: props?.colors?.[3],
        data:
          selected === t('dashboard.percentage')
            ? props.response?.stackedItems?.percentage?.[3]
            : props.response?.stackedItems?.count?.[3],
      },
    ],
  }
  let stackedOptions = {
    type: 'bar',
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 0.8,
    interaction: { intersect: false, mode: 'index' },

    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: function (context: any) {
            return context[0].label.split(',').join('\n')
          },
        },
      },
    },

    scales: {
      x: {
        stacked: true,
        ticks: {
          color: '#495057',
          beginAtZero: true,
          minRotation: 90,
          font: {
            family: "'Inter', serif",
            size: 12,
          },
        },
        grid: {
          color: '#ebedef',
        },
      },
      y: {
        stacked: true,
        ticks: {
          color: '#495057',
        },
        grid: {
          color: '#ebedef',
        },
      },
    },
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <div
          style={{
            fontWeight: 'bold',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            marginLeft: '1rem',
            fontStyle: 'italic',
          }}>
          <span style={{ marginRight: '2rem', fontWeight: 'bold', fontSize: '1rem' }}>
            {selected === 'Yüzde' && '%'}{' '}
          </span>

          <span className="d-block ml-3">{props.tooltipLabel}</span>

          {props.tabIndex === 3 && (
            <div className="d-flex flex-row align-items-center">
              <div
                className="d-flex align-items-center justify-content-center mx-1"
                onMouseOver={(e) => showProperties(e)}
                onMouseLeave={(e) => hideProperties(e)}>
                <i className="pi pi-info-circle info-icon ms-1"></i>
              </div>
              <OverlayPanel ref={opProperties} id="overlay_panel" className="info-popup">
                <div className="p-3">
                  <div className="info-title">{t('overlaypanel-texts.info-header')}</div>

                  <div className="info-text">
                    <li>{t('overlaypanel-texts.channel-panel-text')}</li>
                  </div>
                </div>
              </OverlayPanel>
            </div>
          )}
        </div>

        {props.tabIndex !== 1 && (
          <div className="button-container">
            <SwitchButtonComponent selected={selected} setSelected={setSelected} unselectable={false} />
          </div>
        )}

        {props.tabIndex === 1 && (
          <div className="d-flex">
            <div className="d-flex gap-1">
              <Checkbox
                inputId="dedicated"
                value={'Dedicated'}
                onChange={onCheckedValueChange}
                className="dedicated"
                checked={props.checkedValue.indexOf('Dedicated') !== -1}
                icon={'pi circle-on'}
              />
              <label htmlFor="dedicated" className="dedicatedLabel">
                {t('dashboard.auto-res-inc')}
              </label>
            </div>
            <div className="d-flex gap-1">
              <Checkbox
                inputId="notdedicated"
                icon={'pi circle-on'}
                value={'NotDedicated'}
                onChange={onCheckedValueChange}
                checked={props.checkedValue.indexOf('NotDedicated') !== -1}
                className="dedicated"
              />
              <label htmlFor="notdedicated" className="dedicatedLabel">
                {t('dashboard.auto-res-not-inc')}
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="scroll" style={{ position: 'relative' }}>
        <Chart
          id={props.chartId}
          type="bar"
          className="stacked-chart"
          style={
            props.userInfo !== 'LocationAdmin'
              ? {
                  width: `${props.response?.stackedItems && props.response.stackedItems.labels.length * 100}px`,
                }
              : {
                  width: `${props.response?.stackedItems && props.response.stackedItems.labels.length * 100}px`,
                }
          }
          data={stackedData}
          options={stackedOptions}
        />
      </div>
    </div>
  )
}

export default StackedChart
